/**
 * React Query-style data fetching for the legacy components that expect
 * prop-driven data. Each page fetches via `api()` then drills props down.
 *
 * Components themselves are unchanged from the legacy Express version,
 * which means they keep their prop shapes intact (authToken, config, prs,
 * onSelectPr, etc.) — we just populate them with real Worker data.
 *
 * Org tenancy: every org-scoped hook reads `useOrgId()` and (a) waits until an
 * org is resolved, (b) passes the org id explicitly to `api()` (deterministic —
 * does not depend on the module-bridge effect ordering), and (c) lists `orgId`
 * in its effect deps so switching org refetches + clears stale data. Global
 * surfaces (useConfig singleton, useNixorTasks admin control plane) stay unscoped.
 */
import { useCallback, useEffect, useState } from 'react';
import { api } from './api';
import { useOrg } from './org-context';
import type { WorkflowGraph } from '../components/workflows/types';
import type { ReviewComment } from '../types';

/** The currently-selected org id, or null while the OrgProvider is still
 *  resolving (hooks gate on this so the first fetch carries the right X-Org-Id). */
export function useOrgId(): string | null {
  return useOrg().current?.orgId ?? null;
}

export interface PrSummary {
  id: string;
  title: string;
  description: string;
  author: string;
  authorAvatar: string;
  branch: string;
  targetBranch: string;
  status: 'open' | 'reviewed' | 'merged' | 'closed';
  createdAt: string;
  files: { filepath: string; additions: number; deletions: number; content: string; diff: string }[];
  reviewStatus?: any;
  repoOwner?: string | null;
  repoName?: string | null;
  prNumber?: number | null;
}

export interface PrDetail {
  pr: PrSummary;
  report: any | null;
}

export function usePRs() {
  const orgId = useOrgId();
  const [prs, setPrs] = useState<PrSummary[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!orgId) return; // wait for the org to resolve before fetching
    let cancelled = false;
    setPrs(null);
    setLoading(true);
    (async () => {
      try {
        const rows = await api<PrSummary[]>('/api/prs', {}, orgId);
        if (!cancelled) { setPrs(rows); setError(null); }
      } catch (e: any) { if (!cancelled) setError(e.message); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [orgId]);
  return { prs, loading, error };
}

export function usePR(id: string | null) {
  const orgId = useOrgId();
  const [data, setData] = useState<PrDetail | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!id) { setLoading(false); return; }
    if (!orgId) return; // org still resolving — keep loading true
    let cancelled = false;
    setData(null);
    setLoading(true);
    (async () => {
      // PR ids are `real-${owner}/${repo}#${number}` (webhook.ts) — the `/`
      // and `#` must be encoded or `/api/prs/:id` sees two segments + a
      // fragment and 404s.
      try {
        const d = await api<PrDetail>(`/api/prs/${encodeURIComponent(id)}`, {}, orgId);
        if (!cancelled) setData(d);
      } catch { /* PR not in this org / not found */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [id, orgId]);
  return { data, loading };
}

export function useSchedules() {
  const orgId = useOrgId();
  const [schedules, setSchedules] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    setSchedules(null);
    setLoading(true);
    (async () => {
      try { setSchedules(await api<any[]>('/api/schedules', {}, orgId)); }
      catch { /* leave null */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [orgId]);
  return { schedules, loading };
}

/** Global singleton (github_config) — not org-scoped. */
export function useConfig() {
  const [config, setConfig] = useState<any | null>(null);
  useEffect(() => {
    (async () => {
      try { setConfig(await api<any>('/api/config')); }
      catch { /* ignore */ }
    })();
  }, []);
  return config;
}

export async function triggerReview(prId: string) {
  return api<{ message: string }>('/api/review/trigger', {
    method: 'POST',
    body: JSON.stringify({ prId }),
  });
}

export async function interactReview(prId: string, commentId: string, action: 'accept' | 'refine' | 'dismiss' | 'apply', userFeedback?: string) {
  return api<{ message: string; comment?: ReviewComment }>('/api/review/interact', {
    method: 'POST',
    body: JSON.stringify({ prId, commentId, action, userFeedback }),
  });
}

export async function resetDB() {
  return api<{ message: string }>('/api/prs/reset', { method: 'POST' });
}

export async function toggleSchedule(id: string) {
  return api<any>(`/api/schedules/${id}/toggle`, { method: 'POST' });
}

export async function runSchedule(id: string) {
  return api<any>(`/api/schedules/${id}/run`, { method: 'POST' });
}

export interface AgentStatus {
  pending: number;
  running: number;
  inFlight: { taskId: string; prId: string; repo: string; prNumber: number; status: string; ageMs: number }[];
  recentReviews: { prId: string; score: number; conclusion: string; timestamp: string; title: string | null; repo: string | null; prNumber: number | null }[];
  recentPRs: { id: string; title: string; repo: string | null; prNumber: number | null; status: string; reviewStatus: any; createdAt: string }[];
}

export function useAgentStatus() {
  const orgId = useOrgId();
  const [status, setStatus] = useState<AgentStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const retry = useCallback(() => setNonce((n) => n + 1), []);
  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setInterval>;
    setStatus(null);
    setLoading(true);
    async function tick() {
      try {
        const s = await api<AgentStatus>('/api/agents/status', {}, orgId);
        if (!cancelled) { setStatus(s); setError(null); setLoading(false); }
      } catch (e) {
        // Surface the real failure (e.g. DB_ERROR detail) so the queue +
        // recent-reviews cards can show what's wrong instead of reading as
        // "no data". Polling keeps trying; the error only changes the UI.
        if (!cancelled) { setError(e instanceof Error ? e.message : 'Failed to load agent status'); setLoading(false); }
      }
    }
    tick();
    timer = setInterval(tick, 4000);
    return () => { cancelled = true; clearInterval(timer); };
    // nonce drives the manual retry; poll continues on its own interval.
  }, [orgId, nonce]);
  return { status, loading, error, retry };
}

// ---------- nixor-edge Edge Queue (admin control plane — not org-scoped) ----------

export interface NixorTask {
  taskId: string;
  status: 'pending' | 'running' | 'completed';
  prompt: string;
  createdAt: number;
  startedAt: number | null;
}

export interface NixorTasksResponse {
  tasks: NixorTask[];
  queueLength: number | null;
}

/** Poll nixor-edge pending+running tasks every 3s. `retry` forces an immediate
 *  refetch (used after trigger/cancel/clear so the UI updates without a wait).
 *  Surfaces `error` so the UI can flag a misconfigured proxy instead of looking
 *  empty. Global (admin) — not org-scoped. */
export function useNixorTasks() {
  const [data, setData] = useState<NixorTasksResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setInterval>;
    async function tick() {
      try {
        const res = await api<NixorTasksResponse>('/api/nixor/tasks');
        if (!cancelled) { setData(res); setError(null); setLoading(false); }
      } catch (e: any) {
        if (!cancelled) { setError(e?.message ?? 'fetch failed'); setLoading(false); }
      }
    }
    tick();
    timer = setInterval(tick, 3000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [nonce]);
  return { data, loading, error, retry: () => setNonce((n) => n + 1) };
}

export async function triggerNixorTask(prompt: string, labels?: Record<string, string>) {
  return api<{ success: boolean; taskId: string }>('/api/nixor/trigger', {
    method: 'POST',
    body: JSON.stringify({ prompt, labels: labels ?? {} }),
  });
}

export async function cancelNixorTask(taskId: string) {
  return api<{ ok: boolean }>('/api/nixor/cancel', {
    method: 'POST',
    body: JSON.stringify({ taskId }),
  });
}

export async function clearNixorQueue() {
  return api<{ ok: boolean }>('/api/nixor/queue/clear', { method: 'POST' });
}

export async function simulateWebhook(input: { owner: string; repo: string; prNumber: number; baseSha: string; headSha: string; title?: string; body?: string }) {
  return api<{ message: string; prId: string; taskId: string }>('/api/webhook/simulate', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// ---------- Capabilities (skills / MCP / built-in tools / instructions) ----------

/** Agent Skill following the Anthropic Agent Skills (SKILL.md) standard. */
export interface Skill {
  name: string;
  description: string;
  body: string;
  isActive: boolean;
}

export type McpTransport = 'stdio' | 'streamable_http';

/** MCP server. stdio runs `npx`|`uvx` against a package; streamable_http hits a URL. */
export interface McpServer {
  id: string;
  name: string;
  transport: McpTransport;
  isActive: boolean;
  command?: 'npx' | 'uvx';
  package?: string;
  args?: string[];
  env?: Record<string, string>;
  url?: string;
  headers?: Record<string, string>;
  exportedTools?: string[];
}

export interface BuiltInTool {
  id: string;
  name: string;
  description: string;
  usageCount: number;
  safetyTier: 'Auto-Run' | 'Requires Admin Authorization';
  category: 'Read' | 'Write' | 'Execute';
}

export interface CapabilitiesData {
  skills: Skill[];
  mcpServers: McpServer[];
  builtInTools: BuiltInTool[];
  instructions: string;
}

export async function fetchCapabilities(orgId: string) {
  return api<CapabilitiesData>('/api/capabilities', {}, orgId);
}

export async function saveCapabilities(data: CapabilitiesData, orgId: string) {
  return api<{ message: string }>('/api/capabilities', { method: 'PUT', body: JSON.stringify(data) }, orgId);
}

// ---------- Workflows (studio designer) ----------

/** Returns graphs as-is (nodes + edges). Caller runs migrateLegacyGraphs() to
 *  handle rows written by the pre-studio DIY canvas. */
export async function fetchWorkflows(orgId: string) {
  return api<WorkflowGraph[]>('/api/workflows', {}, orgId);
}

export async function saveWorkflows(workflows: WorkflowGraph[], orgId: string) {
  return api<{ message: string }>('/api/workflows', { method: 'PUT', body: JSON.stringify(workflows) }, orgId);
}

// ---------- SBOM projects ----------

export async function fetchSbomProjects(orgId: string) {
  return api<unknown[]>('/api/sbom', {}, orgId);
}

export async function saveSbomProjects(projects: unknown[], orgId: string) {
  return api<{ message: string }>('/api/sbom', { method: 'PUT', body: JSON.stringify(projects) }, orgId);
}

// ---------- Issues (review findings + GitHub issues) ----------

export interface Finding {
  id: string;
  prId: string;
  repoOwner: string | null;
  repoName: string | null;
  prNumber: number | null;
  filepath: string | null;
  line: number;
  message: string;
  severity: string;
  category: string;
  status: string;
  createdAt: string;
}

export interface RepoIssue {
  repo: string;
  number: number;
  title: string;
  htmlUrl: string;
  state: string;
  updatedAt: string | null;
  author: string | null;
  labels: { name: string; color: string }[];
}

export interface RepoSnapshot {
  id: string;
  repoId: string;
  defaultBranch: string | null;
  description: string | null;
  private: boolean;
  language: string | null;
  license: string | null;
  topics: string;
  stars: number;
  forks: number;
  openIssues: number;
  sizeKb: number;
  defaultBranchSha: string | null;
  readme: string | null;
  fileTree: string;
  branches: string;
  issues: string;
  updatedAt: string;
  syncedAt: string;
}

export interface AgentTask {
  id: string;
  source: string;
  sourceId: string;
  orgId: string;
  repoFull: string;
  prNumber: number | null;
  issueNumber: number | null;
  title: string;
  prompt: string;
  status: string;
  sandboxRef: string | null;
  podName: string | null;
  temporalWorkflowId: string | null;
  traceId: string | null;
  result: string | null;
  error: string | null;
  costUsd: string;
  durationMs: number;
  createdAt: string;
  updatedAt: string;
}

export function useAgentTasks(filters: { status?: string; source?: string } = {}) {
  const orgId = useOrgId();
  const [data, setData] = useState<AgentTask[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const retry = useCallback(() => setNonce((n) => n + 1), []);
  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const qs = new URLSearchParams();
        if (filters.status) qs.set('status', filters.status);
        if (filters.source) qs.set('source', filters.source);
        const rows = await api<AgentTask[]>(`/api/tasks${qs.toString() ? `?${qs}` : ''}`, {}, orgId);
        if (!cancelled) { setData(rows); setError(null); }
      } catch (e: any) {
        if (!cancelled) setError(e.message ?? String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orgId, nonce, filters.status, filters.source]);
  return { tasks: data, loading, error, retry };
}

export function useRepoSnapshots() {
  const orgId = useOrgId();
  const [data, setData] = useState<RepoSnapshot[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const retry = useCallback(() => setNonce((n) => n + 1), []);
  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const rows = await api<RepoSnapshot[]>('/api/repo/snapshots', {}, orgId);
        if (!cancelled) { setData(rows); setError(null); }
      } catch (e: any) {
        if (!cancelled) setError(e.message ?? String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orgId, nonce]);
  return { snapshots: data, loading, error, retry };
}

export function useFindings(filters: { severity?: string; category?: string; status?: string }) {
  const orgId = useOrgId();
  const [data, setData] = useState<Finding[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const retry = useCallback(() => setNonce((n) => n + 1), []);
  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    setData(null);
    setLoading(true);
    (async () => {
      try {
        const qs = new URLSearchParams();
        if (filters.severity) qs.set('severity', filters.severity);
        if (filters.category) qs.set('category', filters.category);
        if (filters.status) qs.set('status', filters.status);
        const rows = await api<Finding[]>(`/api/issues/findings${qs.toString() ? `?${qs}` : ''}`, {}, orgId);
        if (!cancelled) { setData(rows); setError(null); }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load findings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.severity, filters.category, filters.status, orgId, nonce]);
  return { findings: data, loading, error, retry };
}

export interface IssueFixRun {
  id: string;
  taskId: string;
  orgId: string;
  repoOwner: string;
  repoName: string;
  issueNumber: number;
  issueTitle: string;
  baseBranch: string;
  status: string;
  branch: string | null;
  prNumber: number | null;
  prUrl: string | null;
  error: string | null;
  createdAt: string;
  updatedAt: string;
}

export function useIssueFixRuns() {
  const orgId = useOrgId();
  const [data, setData] = useState<IssueFixRun[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const retry = useCallback(() => setNonce((n) => n + 1), []);
  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const rows = await api<IssueFixRun[]>('/api/issues/fix-runs', {}, orgId);
        if (!cancelled) { setData(rows); setError(null); }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load fix runs');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orgId, nonce]);
  return { runs: data, loading, error, retry };
}

/** Dispatch an issue-fix task for a GitHub issue. Returns the taskId. */
export async function dispatchIssueFix(input: { owner: string; repo: string; issueNumber: number; issueTitle?: string }, orgId?: string | null): Promise<{ taskId: string }> {
  return api<{ taskId: string }>('/api/issues/fix', {
    method: 'POST',
    body: JSON.stringify(input),
  }, orgId);
}

export function useGithubIssues() {
  const orgId = useOrgId();
  const [data, setData] = useState<RepoIssue[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const retry = useCallback(() => setNonce((n) => n + 1), []);
  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    setData(null);
    setLoading(true);
    (async () => {
      try {
        const rows = await api<RepoIssue[]>('/api/issues/github', {}, orgId);
        if (!cancelled) { setData(rows); setError(null); }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load issues');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orgId, nonce]);
  return { issues: data, loading, error, retry };
}

// ---------- Reliability (code-quality metrics) ----------

export interface ReliabilityMetrics {
  totalReviews: number;
  defectCount: number;
  defectRate: number;
  findingsPerPr: number;
  avgScore: number;
  totalFindings: number;
  scoreTrend: { ts: string; score: number }[];
  categoryCounts: { category: string; count: number }[];
  severityCounts: { severity: string; count: number }[];
  topFiles: { filepath: string; count: number }[];
}

export function useReliabilityMetrics() {
  const orgId = useOrgId();
  const [data, setData] = useState<ReliabilityMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const retry = useCallback(() => setNonce((n) => n + 1), []);
  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    setData(null);
    setLoading(true);
    (async () => {
      try {
        const m = await api<ReliabilityMetrics>('/api/reliability/metrics', {}, orgId);
        if (!cancelled) { setData(m); setError(null); }
      } catch (e) {
        // Surface the real failure (e.g. DB_ERROR detail) so the UI can show
        // what's wrong instead of a generic "Worker reachable?" guess.
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load reliability metrics');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orgId, nonce]);
  return { metrics: data, loading, error, retry };
}

// ---------- Review rules (org-scoped CRUD catalog) ----------

export interface ReviewRule {
  id: string;
  name: string;
  description: string;
  severity: string;
  category: string;
  enabled: boolean;
  body: string;
  createdAt: string;
  updatedAt: string;
}

export async function fetchReviewRules(orgId: string) {
  return api<ReviewRule[]>('/api/rules', {}, orgId);
}

export async function saveReviewRules(rules: ReviewRule[], orgId: string) {
  return api<{ message: string }>('/api/rules', { method: 'PUT', body: JSON.stringify(rules) }, orgId);
}

// ---------- Projects (repo overview + GitHub-proxy detail) ----------

export interface ProjectOverview {
  owner: string;
  repo: string;
  description: string | null;
  stars: number;
  language: string | null;
  license: string | null;
  defaultBranch: string;
  openIssues: number;
  forks: number;
  htmlUrl: string;
  updatedAt: string | null;
  ownerAvatar: string | null;
  topics: string[];
}
export interface RepoReviewStats {
  reviewCount: number;
  avgScore: number;
  lastReviewAt: string | null;
  lastScore: number | null;
  lastConclusion: string | null;
}
export interface ProjectListItem {
  owner: string;
  repo: string;
  overview: ProjectOverview | null;
  stats: RepoReviewStats;
}
export interface ProjectBranch {
  name: string;
  protected: boolean;
  sha: string | null;
}
export interface ProjectPull {
  number: number;
  title: string;
  state: string;
  htmlUrl: string;
  draft: boolean;
  mergedAt: string | null;
  updatedAt: string | null;
  author: string | null;
  authorAvatar: string | null;
  head: string;
  base: string;
  reviewScore: number | null;
  reviewConclusion: string | null;
}
export interface ProjectIssue {
  number: number;
  title: string;
  htmlUrl: string;
  state: string;
  updatedAt: string | null;
  author: string | null;
  labels: { name: string; color: string }[];
}
export interface ProjectContributor {
  login: string;
  avatar: string | null;
  contributions: number;
}
export interface ProjectMerge {
  number: number;
  title: string;
  mergedAt: string | null;
  author: string | null;
  htmlUrl: string;
}
export interface ProjectCommit {
  sha: string;
  message: string;
  date: string | null;
  author: string | null;
  authorAvatar: string | null;
  htmlUrl: string;
}
export interface ProjectActivity {
  merges: ProjectMerge[];
  commits: ProjectCommit[];
}
export type ProjectSection =
  | 'overview'
  | 'branches'
  | 'pulls'
  | 'issues'
  | 'contributors'
  | 'activity';

export function useProjects() {
  const orgId = useOrgId();
  const [items, setItems] = useState<ProjectListItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    setItems(null);
    setLoading(true);
    (async () => {
      try {
        setItems(await api<ProjectListItem[]>('/api/projects', {}, orgId));
      } catch (e: { message?: string } | unknown) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load projects');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orgId]);
  return { items, loading, error };
}

/** Fetch one detail section; re-fetches when owner/repo/section/org changes.
 *  Pass `enabled: false` to skip the fetch (e.g. when reusing another hook's
 *  data for the same section). */
export function useProjectSection<T>(
  owner: string,
  repo: string,
  section: ProjectSection,
  opts: { enabled?: boolean } = {},
) {
  const orgId = useOrgId();
  const enabled = opts.enabled !== false;
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!enabled || !orgId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setData(null);
    setLoading(true);
    (async () => {
      try {
        const d = await api<T>(
          `/api/projects/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/${section}`,
          {},
          orgId,
        );
        if (!cancelled) {
          setData(d);
          setError(null);
        }
      } catch (e: { message?: string } | unknown) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load section');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [owner, repo, section, enabled, orgId]);
  return { data, loading, error };
}
